import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	CheckOutline,
	Flex,
	HStack,
	PauseSolid,
	PlaySolid,
	rem,
	Slider,
	SliderFilledTrack,
	SliderThumb,
	SliderTrack,
	Text,
	Textarea,
	useColorModeValue,
	VolumeOffSolid,
	VolumeUpSolid,
	XOutline,
} from '@bonusly/design-system';

// eslint-disable-next-line local-rules/no-cross-module-imports
import { useGlobalMediaStore } from '../../../modules/bonus-feed/hooks/use-global-media-store';
import { PulsingAvatarCircles } from '../recording/components/pulsing-avatar-circles';

interface AudioAttachmentPlayerProps {
	audioUrl: string;
	quote?: string;
	giverName?: string;
	giverAvatarUrl?: string;
	duration?: number;
	isEditing?: boolean;
	onQuoteChange?: (quote: string) => void;
	onSaveQuote?: () => void;
	onCancelEdit?: () => void;
}

const formatTime = (seconds: number): string => {
	// Handle invalid values (NaN, Infinity, negative)
	if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
		return '00:00';
	}
	const mins = Math.floor(seconds / 60);
	const secs = Math.floor(seconds % 60);
	return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const AudioAttachmentPlayer: React.FC<AudioAttachmentPlayerProps> = ({
	audioUrl,
	quote,
	giverName,
	giverAvatarUrl,
	duration: initialDuration,
	isEditing = false,
	onQuoteChange,
	onSaveQuote,
	onCancelEdit,
}) => {
	const { t } = useTranslation('recognition');
	const audioId = useId();
	const audioRef = useRef<HTMLAudioElement>(null);
	const audioContextRef = useRef<AudioContext | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
	const animationFrameRef = useRef<number | null>(null);
	const { registerElement, unregisterElement, playVideo: playMedia, pauseVideo: pauseMedia } = useGlobalMediaStore();

	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(initialDuration || 0);
	const [volume] = useState(1);
	const [isMuted, setIsMuted] = useState(false);
	const [audioLevel, setAudioLevel] = useState(0);

	const hasQuote = quote && quote.length > 0;

	// Theme colors - light blue background matching Figma (#D8ECF8 / blue.50)
	const bgColor = useColorModeValue('blue.50', 'gray.800');
	const textColor = useColorModeValue('gray.800', 'white');
	const ringColor = useColorModeValue('#9ECEF0', '#4A90A4');

	// Set up audio analyser for visualizing audio level
	const setupAnalyser = useCallback(() => {
		const audio = audioRef.current;
		if (!audio || audioContextRef.current) return;

		try {
			const audioContext = new AudioContext();
			const analyser = audioContext.createAnalyser();
			analyser.fftSize = 256;
			analyser.smoothingTimeConstant = 0.8;

			const source = audioContext.createMediaElementSource(audio);
			source.connect(analyser);
			analyser.connect(audioContext.destination);

			audioContextRef.current = audioContext;
			analyserRef.current = analyser;
			sourceRef.current = source;
		} catch (error) {
			console.error('Error setting up audio analyser:', error);
		}
	}, []);

	// Animate audio level based on analyser data
	const startAudioLevelAnimation = useCallback(() => {
		if (!analyserRef.current) return;

		const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);

		const animate = () => {
			if (!analyserRef.current || !isPlaying) {
				setAudioLevel(0);
				return;
			}

			analyserRef.current.getByteFrequencyData(dataArray);

			// Calculate RMS (root mean square) for overall loudness
			let sum = 0;
			for (let i = 0; i < dataArray.length; i++) {
				sum += dataArray[i] * dataArray[i];
			}
			const rms = Math.sqrt(sum / dataArray.length);

			// Normalize to 0-1 range with some boost for visibility
			const normalizedLevel = Math.min(1, (rms / 128) * 1.5);
			setAudioLevel(normalizedLevel);

			animationFrameRef.current = requestAnimationFrame(animate);
		};

		animate();
	}, [isPlaying]);

	// Stop audio level animation
	const stopAudioLevelAnimation = useCallback(() => {
		if (animationFrameRef.current) {
			cancelAnimationFrame(animationFrameRef.current);
			animationFrameRef.current = null;
		}
		setAudioLevel(0);
	}, []);

	// Start/stop animation based on playing state
	useEffect(() => {
		if (isPlaying) {
			setupAnalyser();
			startAudioLevelAnimation();
		} else {
			stopAudioLevelAnimation();
		}

		return () => {
			stopAudioLevelAnimation();
		};
	}, [isPlaying, setupAnalyser, startAudioLevelAnimation, stopAudioLevelAnimation]);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
			if (audioContextRef.current) {
				audioContextRef.current.close();
			}
		};
	}, []);

	// Register audio element with global media store so playing this
	// pauses any active video (and vice versa)
	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		registerElement(audio);
		return () => unregisterElement(audio);
	}, [registerElement, unregisterElement]);

	// Sync local isPlaying state when the store pauses us externally
	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		const handlePause = () => setIsPlaying(false);
		audio.addEventListener('pause', handlePause);
		return () => audio.removeEventListener('pause', handlePause);
	}, []);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) return;

		const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
		const handleDurationChange = () => setDuration(audio.duration || initialDuration || 0);
		const handleEnded = () => setIsPlaying(false);
		const handleLoadedMetadata = () => setDuration(audio.duration || initialDuration || 0);

		audio.addEventListener('timeupdate', handleTimeUpdate);
		audio.addEventListener('durationchange', handleDurationChange);
		audio.addEventListener('ended', handleEnded);
		audio.addEventListener('loadedmetadata', handleLoadedMetadata);

		return () => {
			audio.removeEventListener('timeupdate', handleTimeUpdate);
			audio.removeEventListener('durationchange', handleDurationChange);
			audio.removeEventListener('ended', handleEnded);
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
		};
	}, [initialDuration]);

	const handlePlayPause = useCallback(async () => {
		const audio = audioRef.current;
		if (!audio) return;

		if (isPlaying) {
			pauseMedia(audio);
			setIsPlaying(false);
		} else {
			// Reset to beginning if at end
			if (audio.ended) {
				audio.currentTime = 0;
			}
			try {
				// Set up analyser first (before playing) so audio routes through it
				setupAnalyser();

				// Resume AudioContext if it exists and is suspended (required for audio to play through analyser)
				if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
					await audioContextRef.current.resume();
				}

				// Play through global store — this pauses any other active media (video or audio)
				await playMedia(audio);
				setIsPlaying(true);
			} catch (error) {
				console.error('Error playing audio:', error);
			}
		}
	}, [isPlaying, setupAnalyser, playMedia, pauseMedia]);

	const handleSeek = useCallback((value: number) => {
		const audio = audioRef.current;
		if (!audio) return;

		audio.currentTime = value;
		setCurrentTime(value);
	}, []);

	const handleMuteToggle = useCallback(() => {
		const audio = audioRef.current;
		if (!audio) return;

		if (isMuted) {
			audio.volume = volume;
			setIsMuted(false);
		} else {
			audio.volume = 0;
			setIsMuted(true);
		}
	}, [isMuted, volume]);

	// Player controls component (reused in both layouts)
	const PlayerControls = (
		<Box position="absolute" bottom={0} left={0} right={0} background="linear-gradient(to top, rgba(0,0,0,0.5), rgba(0,0,0,0))" px={5} py={4}>
			<Flex align="center" gap={3}>
				{/* Play/Pause Button - dark rounded rectangle with icon + time */}
				<Flex
					as="button"
					onClick={handlePlayPause}
					align="center"
					gap={2}
					bg="gray.800"
					color="white"
					px={4}
					py={2}
					borderRadius="md"
					cursor="pointer"
					_hover={{ bg: 'gray.700' }}
					transition="background 0.2s"
				>
					{isPlaying ? <PauseSolid size="md" color="white" /> : <PlaySolid size="md" color="white" />}
					<Text fontSize="sm" fontWeight="medium" color="white" fontFamily="mono" minWidth="45px">
						{formatTime(currentTime)}
					</Text>
				</Flex>

				{/* Progress Bar */}
				<Box flex={1}>
					<Slider aria-label="Audio progress" value={currentTime} min={0} max={duration || 100} onChange={handleSeek} size="lg">
						<SliderTrack bg="whiteAlpha.300" height="8px" borderRadius="full">
							<SliderFilledTrack bg="white" borderRadius="full" />
						</SliderTrack>
						<SliderThumb boxSize={0} opacity={0} />
					</Slider>
				</Box>

				{/* Volume/Mute Icon */}
				<Box as="button" onClick={handleMuteToggle} color="white" opacity={0.8} _hover={{ opacity: 1 }} cursor="pointer" p={1}>
					{isMuted ? <VolumeOffSolid size="md" /> : <VolumeUpSolid size="md" />}
				</Box>
			</Flex>
		</Box>
	);

	// Layout with quote: Quote on left, avatar on right
	// lg pulsing circles container is 280px, plus padding for controls
	if (hasQuote || isEditing) {
		return (
			<Box className="audio-player" borderRadius="lg" overflow="hidden" width="100%" bg={bgColor} position="relative" minHeight={rem(360)}>
				<Flex height="100%" minHeight={rem(300)}>
					{/* Left: Quote and name (or editable textarea) */}
					<Flex flex={1} direction="column" gap={1} justify="center" pl={6} pr={4} py={8} zIndex={1} maxWidth="55%">
						{isEditing ? (
							<>
								<Textarea
									value={quote || ''}
									onChange={(e) => onQuoteChange?.(e.target.value)}
									placeholder={t('bonus_input.audio.quote_placeholder', 'Enter a pull quote...')}
									fontSize="md"
									color={textColor}
									bg="whiteAlpha.200"
									border="1px solid"
									borderColor="whiteAlpha.300"
									borderRadius="md"
									resize="none"
									rows={4}
									_focus={{ borderColor: 'primary.fg', boxShadow: 'none' }}
								/>
								<HStack mt={2} spacing={2}>
									<Button size="sm" colorScheme="primary" onClick={onSaveQuote} leftIcon={<CheckOutline size="xs" />}>
										{t('bonus_input.audio.save_quote', 'Save')}
									</Button>
									<Button size="sm" variant="ghost" onClick={onCancelEdit} leftIcon={<XOutline size="xs" />}>
										{t('bonus_input.audio.cancel', 'Cancel')}
									</Button>
								</HStack>
							</>
						) : (
							<>
								<Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="normal" color={textColor} lineHeight="tall" letterSpacing="-0.2px" noOfLines={4}>
									&ldquo;{quote}&rdquo;
								</Text>
								{giverName && (
									<Text fontSize="sm" fontWeight="semibold" color={textColor} mt={1} opacity={0.8}>
										— {giverName}
									</Text>
								)}
							</>
						)}
					</Flex>

					{/* Right: Avatar with solid rings */}
					<Flex align="center" justify="center" pr={4} flexShrink={0}>
						<PulsingAvatarCircles
							avatarUrl={giverAvatarUrl}
							avatarName={giverName}
							isAnimating={isPlaying}
							size="lg"
							solidRings={true}
							ringColor={ringColor}
							audioLevel={audioLevel}
						/>
					</Flex>
				</Flex>

				{PlayerControls}
				<audio id={audioId} ref={audioRef} src={audioUrl} preload="metadata" crossOrigin="anonymous" />
			</Box>
		);
	}

	// Simple layout: Centered avatar (no quote)
	// lg pulsing circles container is 280px, plus padding for controls
	return (
		<Box className="audio-player" borderRadius="lg" overflow="hidden" width="100%" bg={bgColor} position="relative" minHeight={rem(360)}>
			<Flex height={rem(300)} align="center" justify="center">
				<PulsingAvatarCircles
					avatarUrl={giverAvatarUrl}
					avatarName={giverName}
					isAnimating={isPlaying}
					size="lg"
					solidRings={true}
					ringColor={ringColor}
					audioLevel={audioLevel}
				/>
			</Flex>

			{PlayerControls}
			<audio id={audioId} ref={audioRef} src={audioUrl} preload="metadata" crossOrigin="anonymous" />
		</Box>
	);
};
